import {type ThemeIconKey} from 'platform/foundation';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';

import {useGetVehicleQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {useVehicleCatalogue} from '@dms/shared';

import {parseDate} from 'shared/web';

export interface ListingParameter {
  icon: ThemeIconKey;
  label: string;
  value: string | null | undefined;
}

// The vehicle parameters that the auction listing shows to the buyers.
export function useListingParameters(vehicleId: string): ListingParameter[] {
  const {data: vehicle} = useGetVehicleQuery({vehicleId});
  const [vehicleUtils] = useVehicleCatalogue(vehicle?.type);
  const formatNumber = useFormatNumber();
  const formatDateTime = useDateTimeFormatter();
  const firstRegistrationOn = vehicle?.vehicle.firstRegistrationOn;
  const mileage = vehicle?.vehicle.mileage;

  return [
    {
      icon: 'specification/year',
      label: i18n.t('entity.auction.listing.firstRegistration'),
      value: firstRegistrationOn
        ? formatDateTime('dateShort', parseDate(firstRegistrationOn))
        : null,
    },
    {
      icon: 'specification/mileage',
      label: i18n.t('entity.auction.listing.mileage'),
      value:
        mileage === null || mileage === undefined
          ? null
          : `${formatNumber(mileage)} ${i18n.t('general.metric.km')}`,
    },
    {
      icon: 'specification/transmission',
      label: i18n.t('entity.auction.listing.gearbox'),
      value: vehicle?.transmission ? vehicleUtils.getTransmission(vehicle.transmission) : null,
    },
    {
      icon: 'specification/fuel_type',
      label: i18n.t('entity.auction.listing.fuel'),
      value: vehicle?.fuelType ? vehicleUtils.getFuelType(vehicle.fuelType) : null,
    },
    {
      icon: 'specification/power',
      label: i18n.t('entity.auction.listing.power'),
      value:
        vehicle?.power === null || vehicle?.power === undefined
          ? null
          : `${formatNumber(vehicle.power)} ${i18n.t('general.metric.kW')}`,
    },
    {
      icon: 'specification/drive_type',
      label: i18n.t('entity.auction.listing.drive'),
      value: vehicle?.drive ? vehicleUtils.getDrive(vehicle.drive) : null,
    },
    {
      icon: 'editor/attach_money',
      label: i18n.t('entity.auction.listing.vatDeductible'),
      value:
        vehicle?.isVatDeductible === null || vehicle?.isVatDeductible === undefined
          ? null
          : i18n.t(vehicle.isVatDeductible ? 'general.labels.yes' : 'general.labels.no'),
    },
  ];
}
